import React from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import AddAccountCardModal from '../../../Widgets/AddAccountCardModal/AddAccountCardModal';
import EnumsService from '../../../../Services/enums.service';

const MdbMissingAccountCardsTable = ({ 
    missingAccountCards, 
    direction,
    client,
    token,
    palCodes = [],
    accountCardOptions = [],
    fetchEnums,
    setLoading,
    isRange = false,
    isClosedReportingMonth = false,
    sortCodes = [],
    user,
    setAccountCardOptions,
    onCardCreated
}) => {
    const { t } = useTranslation();
    const [modalOpen, setModalOpen] = React.useState(false);
    const [selectedCard, setSelectedCard] = React.useState(null);
    const palCodeTypeOptions = (palCodes || []).map(item => ({
        label: item.palCodeId,
        value: item.palCodeId
    }));
    // Filter out duplicate card IDs - keep only unique cardId values
    const uniqueCards = React.useMemo(() => {
        if (!missingAccountCards || missingAccountCards.length === 0) {
            return [];
        }
        const seen = new Set();
        return missingAccountCards.filter(card => {
            const cardId = card.cardId || card.CardId;
            if (!cardId || seen.has(cardId)) {
                return false;
            }
            seen.add(cardId);
            return true;
        });
    }, [missingAccountCards]);

    const handleCreateCard = (card) => {
        const cardId = card.cardId || card.CardId;
        setSelectedCard({
            accountCodeId: cardId,
            accountCodeName: card.cardName || card.CardName || '',
            accountCaseId: '',
            palCode: ''
        });
        setModalOpen(true);
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedCard(null);
    };

    const handleChangeAfterAdd = async (cardId, type) => {
        // This will be called after a card is successfully created
        // Refresh account card options
        if (client && token) {
            try {
                const accountsResponse = await EnumsService.GetAccountCardsByCaseId(client.caseId, token);
                if (accountsResponse.ok) {
                    const accountsData = await accountsResponse.json();
                    const accountCardOptions = [{ name: t('AddAccountCard'), value1: 'AddItem' }, ...accountsData];
                    if (setAccountCardOptions) {
                        setAccountCardOptions(accountCardOptions);
                    }
                }
            } catch (error) {
                console.error('Error refreshing account cards:', error);
            }
        }
        // Call parent fetchEnums if provided
        if (fetchEnums) {
            await fetchEnums();
        }
        // Notify parent to refresh the MDB preview
        if (onCardCreated) {
            onCardCreated();
        }
    };

    if (!missingAccountCards || missingAccountCards.length === 0 || uniqueCards.length === 0) {
        return (
            <p style={{ color: 'green', marginTop: 20 }}>
                {t('NoMissingAccountCards') || 'No missing account cards found'}
            </p>
        );
    }

    return (
        <div style={{ marginTop: 25 }}>
            <h5>{t('MissingAccountCards') || 'Missing Account Cards'}</h5>
            <div style={{ maxHeight: 400, overflow: 'auto' }}>
                <table className="sortcode-table editable">
                    <thead>
                        <tr>
                            <th style={{ width: '25%' }}>{t('CardId') || 'Card ID'}</th>
                            <th style={{ width: '50%' }}>{t('CardName') || 'Card Name'}</th>
                            {client && token && (
                                <th style={{ width: '25%' }}>{t('Actions') || 'פעולות'}</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {uniqueCards.map((card, index) => (
                            <tr key={`${card.cardId || card.CardId || ''}-${card.cardName || card.CardName || ''}-${index}`}>
                                <td>
                                    <TextField
                                        fullWidth
                                        value={card.cardId || card.CardId || ''}
                                        variant="standard"
                                        InputProps={{
                                            readOnly: true,
                                            disableUnderline: true,
                                            sx: {
                                                height: '48px',
                                                padding: '0 10px',
                                                borderRadius: '8px',
                                                background: '#F5F5F6',
                                                border: '1px solid transparent',
                                            },
                                        }}
                                    />
                                </td>
                                <td>
                                    <TextField
                                        fullWidth
                                        value={card.cardName || card.CardName || ''}
                                        variant="standard"
                                        InputProps={{
                                            readOnly: true,
                                            disableUnderline: true,
                                            sx: {
                                                height: '48px',
                                                padding: '0 10px',
                                                borderRadius: '8px',
                                                background: '#F5F5F6',
                                                border: '1px solid transparent',
                                            },
                                        }}
                                        inputProps={{
                                            style: {
                                                textAlign: direction === 'ltr' ? 'left' : 'right',
                                            },
                                        }}
                                    />
                                </td>
                                {client && token && (
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => handleCreateCard(card)}
                                            sx={{
                                                backgroundColor: '#304FFF',
                                                color: '#fff',
                                                '&:hover': {
                                                    backgroundColor: '#2030cc'
                                                },
                                                fontSize: '0.75rem',
                                                padding: '4px 8px',
                                                minWidth: 'auto'
                                            }}
                                        >
                                            {t('CreateCard') || 'צור כרטיס'}
                                        </Button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p style={{ marginTop: 12, fontSize: '14px', color: '#666' }}>
                {t('MissingAccountCardsNote') || 'These account cards are referenced in the MDB file but do not exist in the system. Please import account cards first or create them manually.'}
            </p>

            {client && token && (
                <AddAccountCardModal
                    open={modalOpen}
                    onClose={handleModalClose}
                    editedItem={selectedCard}
                    isRange={isRange}
                    isClosedReportingMonth={isClosedReportingMonth}
                    palCodes={palCodeTypeOptions}
                    propPalCode={null}
                    direction={direction}
                    client={client}
                    token={token}
                    setLoading={setLoading}
                    accountCardOptions={accountCardOptions}
                    handleChangeAfterAdd={handleChangeAfterAdd}
                    fetchEnums={fetchEnums}
                    type="main"
                    isEdit={false}
                    sortCodes={sortCodes}
                />
            )}
        </div>
    );
};

export default MdbMissingAccountCardsTable;








